import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, ShieldCheck } from "lucide-react";

export default function Settings() {
  const [cfg, setCfg] = useState({ razorpay_key_id: "", has_secret: false });
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setCfg((await api.get("/admin/settings")).data);
    setUsers((await api.get("/admin/users")).data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings", {
        razorpay_key_id: key || cfg.razorpay_key_id,
        razorpay_key_secret: secret || undefined,
      });
      toast.success("Settings saved");
      setSecret("");
      load();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const setRole = async (user_id, role) => {
    await api.patch("/admin/users/role", { user_id, role });
    toast.success("Role updated");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="settings-page">
      <Link to="/admin" className="label-mono inline-flex items-center gap-2"><ArrowLeft size={14}/>Back to console</Link>
      <h1 className="font-display font-black text-4xl mt-4">Settings</h1>

      <section className="mt-10 glass rounded-2xl p-6" data-testid="razorpay-settings">
        <div className="flex items-center gap-3">
          <KeyRound size={18} className="text-violet-300"/>
          <div>
            <div className="font-display font-bold text-xl">Razorpay API keys</div>
            <div className="text-white/60 text-sm">Add your <span className="font-mono">Key ID</span> and <span className="font-mono">Key Secret</span> when ready. Payments activate automatically after validation.</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div>
            <div className="label-mono mb-2">Key ID</div>
            <input placeholder={cfg.razorpay_key_id || "Enter payment Key ID"}
              value={key} onChange={e=>setKey(e.target.value)}
              data-testid="razorpay-key-id"
              className="w-full bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 font-mono text-sm"/>
          </div>
          <div>
            <div className="label-mono mb-2">Key Secret {cfg.has_secret && <span className="chip emerald ml-2">Stored</span>}</div>
            <input placeholder={cfg.has_secret ? "•••••••• (encrypted)" : "paste secret"}
              value={secret} onChange={e=>setSecret(e.target.value)}
              data-testid="razorpay-key-secret"
              className="w-full bg-transparent hairline-strong rounded-lg px-4 py-3 outline-none focus:border-violet-500 font-mono text-sm"/>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button onClick={save} disabled={busy} className="pill-btn primary" data-testid="settings-save">
            {busy ? "Saving…" : "Save settings"}
          </button>
          <span className="text-xs text-white/40 flex items-center gap-2"><ShieldCheck size={12}/> Secret is AES-encrypted at rest.</span>
        </div>
      </section>

      <section className="mt-12 glass rounded-2xl p-6" data-testid="team-settings">
        <div className="font-display font-bold text-xl">Team & roles</div>
        <div className="text-white/60 text-sm">Promote users into Designer, Developer, Sales, Marketing or Super Admin roles.</div>
        <div className="mt-6 divide-y divide-white/[0.06]">
          {users.map(u => (
            <div key={u.user_id} className="py-3 flex items-center justify-between" data-testid={`user-row-${u.user_id}`}>
              <div>
                <div className="font-semibold">{u.name} <span className="text-white/50 text-xs font-mono ml-2">{u.email}</span></div>
                <div className="text-xs text-white/50 font-mono">{u.provider} · {u.user_id}</div>
              </div>
              <select value={u.role} onChange={e=>setRole(u.user_id, e.target.value)}
                data-testid={`role-select-${u.user_id}`}
                className="bg-[#12121A] hairline-strong rounded-lg px-3 py-2 text-sm">
                {["client","designer","developer","marketing","sales","super_admin"].map(r=><option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
